import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import {
  SalesRecording,
  TripHistories,
  TripMatch,
  TripMatchStatus,
  TripRequest,
  TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import TripDetailsView from './trip-details-view';
import { TripError } from '../../components/trips';
import { formatCountryNames } from '../../utils/helpers';
import { useAuthStore } from '../../store';

const TripDetailsContainer = () => {
  const { tripRequestId } = useParams();
  const { loggedInTraveler } = useAuthStore();
  const travelerId = loggedInTraveler?.id;

  const apiClient = useApiClient();

  const [allSalesRecordings, setAllSalesRecordings] = useState<{ [key: string]: SalesRecording[] }>({});
  const [salesRecordingsLoading, setSalesRecordingsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [viewingCoTravelerCardId, setViewingCoTravelerCardId] = useState<string | null>(null);
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: tripHistories } = useFetchImmutable<TripHistories>(apiClient.peopleClient.getTravelerTripHistories, [travelerId], !!travelerId);

  const {
    data: tripRequest,
    isLoading: tripRequestLoading,
    mutate,
  } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);

  const {
    data: matches,
    isLoading: matchesLoading,
  } = useFetch<TripMatch[]>(apiClient.tripRequestsClient.getTripRequestTripMatches, [tripRequestId], !!tripRequestId);

  const toggleCancel = () => { setIsCancelling(!isCancelling); window.scrollTo(0, 0); };
  const toggleInvite = () => { setIsInviting(!isInviting); window.scrollTo(0, 0); };
  const openCoTravelerCard = (coTravelerId: string | null) => { setViewingCoTravelerCardId(coTravelerId); };

  const activeMatches = useMemo(() => {
    if (matchesLoading || !matches || !countryCodes) return [];

    const activeStatuses = [
      TripMatchStatus.MatchActive,
      TripMatchStatus.MatchPostponed,
      TripMatchStatus.MatchInactive,
      TripMatchStatus.MatchSold,
      TripMatchStatus.MatchPartnerCanceled,
      TripMatchStatus.MatchTravelerCanceled,
      TripMatchStatus.MatchSoldCanceled,
      TripMatchStatus.MatchRefundedCanceled,
    ];

    return matches.filter((match) => activeStatuses.includes(match.status));
  }, [matches, matchesLoading, countryCodes]);

  const soldMatches = useMemo(
    () => activeMatches.filter((match) => match.status === TripMatchStatus.MatchSold),
    [activeMatches],
  );

  const fetchSalesRecordings = async (matchesToFetch: TripMatch[]) => {
    setSalesRecordingsLoading(true);
    try {
      const recordings = await Promise.allSettled(
        matchesToFetch.map(async (match) => {
          try {
            const matchRecordings = await apiClient.tripMatchesClient.getTripMatchSalesRecordings(match.id);
            return { matchId: match.id, recordings: matchRecordings };
          } catch (error) {
            console.error(`Error fetching recordings for match ${match.id}:`, error);
            return { matchId: match.id, recordings: [] };
          }
        }),
      );

      const newRecordings = recordings.reduce((acc, result) => {
        if (result.status === 'fulfilled') {
          acc[result.value.matchId] = result.value.recordings;
        }
        return acc;
      }, {} as { [key: string]: SalesRecording[] });

      setAllSalesRecordings(newRecordings);
    } catch (error) {
      console.error('Error fetching sales recordings:', error);
      setAllSalesRecordings({});
    } finally {
      setSalesRecordingsLoading(false);
    }
  };

  const cancellationCountry = useMemo(() => {
    if (!tripRequest) return '';
    // If not sold, use the tripRequest destinations:
    if (tripRequest.status !== TripRequestStatus.SoldStatus) {
      return tripRequest.destinations
        .map((d) => d.name)
        .join(',');
    }

    // If sold, derive from soldMatches:
    const allSoldCountries = Array.from(
      new Set(soldMatches.flatMap((match) => match.countries || [])),
    );
    return allSoldCountries.join(',');
  }, [tripRequest, soldMatches]);

  const titleCountry = useMemo(() => {
    if (!tripRequest) return '';

    // If the trip is not sold, derive title from tripRequest.destinations
    if (tripRequest.status !== TripRequestStatus.SoldStatus) {
      return tripRequest.destinations.map((destination) => destination.name).join(', ');
    }

    // If sold, compute the country list from soldMatches
    const allSoldCountries = Array.from(
      new Set(soldMatches.flatMap((match) => match.countries ?? [])),
    );

    return allSoldCountries.length > 0 ? formatCountryNames(allSoldCountries, countryCodes) : '';
  }, [tripRequest, soldMatches, countryCodes]);

  useEffect(() => {
    if (soldMatches.length) {
      fetchSalesRecordings(soldMatches);
    }
  }, [soldMatches]);

  const isCooperative = matches?.some((match) => match.cooperative) || false;
  const canInviteCoTravelers = useMemo(() => {
    if (!tripRequest || !tripHistories) return false;
    return tripHistories.activeTrips?.some((trip) => trip.id === tripRequest.id);
  }, [tripRequest, tripHistories]);

  if (!tripRequest && !tripRequestLoading) {
    return <TripError />;
  }

  if (!tripRequest || !soldMatches) {
    return null;
  }

  return (
    <TripDetailsView
      activeMatches={activeMatches}
      canInviteCoTravelers={canInviteCoTravelers}
      cooperative={isCooperative}
      photoId={tripRequest.photoId}
      isLoading={tripRequestLoading || matchesLoading || salesRecordingsLoading}
      salesRecordings={allSalesRecordings}
      soldMatches={soldMatches}
      status={tripRequest.status}
      titleCountry={titleCountry}
      countryForCancellation={cancellationCountry}
      tripRequest={tripRequest}
      travelerId={travelerId || ''}
      isCancelling={isCancelling}
      toggleCancel={toggleCancel}
      isInviting={isInviting}
      toggleInvite={toggleInvite}
      openCoTravelerCard={openCoTravelerCard}
      viewingCoTravelerCardId={viewingCoTravelerCardId}
      mutate={mutate}
    />
  );
};

export default TripDetailsContainer;
